import React, { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import Modal from 'react-responsive-modal'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { BiTrash } from 'react-icons/bi'
import { IoIosAdd, IoIosClose } from 'react-icons/io'
import { createTypesOfVaccinations, updateTypesOfVaccinations, deleteTypesOfVaccinations } from '../../actions/medCenter/vassinations'
import { Input } from '../../components/ModalInputs'
import Button from '../../components/Button'
import Table from '../../components/Table'
import Confirmation from '../../components/Confirmation/Confirmation'
import styles from './styles.module.scss'

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Обязательное поле')
    .min(4, 'Минимум 4 символа')
    .max(30, 'Не более 30 символов')
})

const VaccineTypesModal = ({ onClose, typesVaccinations }) => {
  const dispatch = useDispatch()
  const [selectedType, setSelectedType] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const isEditMode = selectedType !== null
  const isAddMode = selectedType === 'add'

  const openEditModal = useCallback((item) => () => {
    setSelectedType(item || 'add')
  }, [])

  const closeEditModal = useCallback(() => {
    setSelectedType(null)
  }, [])

  const handleDelete = useCallback((item) => (e) => {
    e.stopPropagation()
    setConfirmation({ id: item.c_vac_type, name: item.name })
  }, [])

  const acceptConfirmation = useCallback(async () => {
    if (!confirmation) return
    setIsDeleting(true)
    try {
      await dispatch(deleteTypesOfVaccinations(confirmation.id))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const createItem = useCallback(async (values) => {
    await dispatch(createTypesOfVaccinations(values))
    closeEditModal()
  }, [dispatch, closeEditModal])

  const updateItem = useCallback(async (values) => {
    await dispatch(updateTypesOfVaccinations(values))
    closeEditModal()
  }, [dispatch, closeEditModal])

  const {
    values,
    errors,
    touched,
    submitCount,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
    setValues
  } = useFormik({
    initialValues: { name: '' },
    enableReinitialize: true,
    validationSchema,
    onSubmit: isAddMode ? createItem : updateItem
  })

  const openForEdit = useCallback((item) => () => {
    if (item === 'add') {
      resetForm()
      setSelectedType('add')
    } else {
      setValues(item)
      setSelectedType(item)
    }
  }, [resetForm, setValues])

  const getError = useCallback((fieldName) =>
    (touched[fieldName] || submitCount > 0) && errors[fieldName],
    [touched, errors, submitCount]
  )

  return (
    <Modal
      open={true}
      onClose={onClose}
      classNames={{ modal: styles.typesModal }}
      showCloseIcon={false}
      center
    >
      <div className={styles.typesModalContent}>
        <h3>Типы вакцин</h3>

        {isEditMode ? (
          <form onSubmit={handleSubmit} className={styles.typeEditForm}>
            <Input
              label="Название"
              name="name"
              value={values.name || ''}
              error={getError('name')}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <div className={styles.typeEditButtons}>
              <Button type="submit" mode="white">
                {isAddMode ? 'Добавить' : 'Сохранить'}
              </Button>
              <Button type="button" mode="white" onClick={closeEditModal}>
                Отмена
              </Button>
            </div>
          </form>
        ) : (
          <>
            <Table className={styles.typesTable}>
              <thead>
                <tr>
                  <th>Тип вакцины</th>
                  <th style={{ width: '60px' }}></th>
                </tr>
              </thead>
              <tbody>
                {typesVaccinations?.map((item) => (
                  <tr
                    key={item.c_vac_type}
                    onClick={openForEdit(item)}
                    className={styles.tableRow}
                  >
                    <td>{item.name}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.deleteBtn}
                        onClick={handleDelete(item)}
                      >
                        <BiTrash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className={styles.typesActions}>
              <Button onClick={openForEdit('add')}>
                <IoIosAdd size={20} style={{ marginRight: 4 }} />
                Добавить
              </Button>
              <Button onClick={onClose}>
                <IoIosClose size={20} style={{ marginRight: 4 }} />
                Закрыть
              </Button>
            </div>
          </>
        )}
      </div>

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить ${confirmation.name}?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </Modal>
  )
}

export default VaccineTypesModal
